import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { bubEvents } from 'javascripts/shared/events/bub_events';
import classNames from 'classnames';
import { graphQLQueries } from 'javascripts/shared/graphql/queries';
import { useGraphQLClientData } from 'javascripts/hooks/use-graphql-client-data';

import { ShoppingCartContext } from 'javascripts/contexts/shopping_cart_context';
import { AddToCartButton } from 'javascripts/components/add_to_cart_button';
import { GiftAudiobookButton } from 'javascripts/components/gift_audiobook_button';
import { GeneralSharingButton } from 'javascripts/components/general_sharing_button';
import { Loader } from 'javascripts/components/loader';
import { WishlistButton } from 'javascripts/components/wishlist_button';

import styles from 'stylesheets/components/audiobook/audiobook_buttons.module';

export function AudiobookButtons({ audiobookId, giftPurchaseUrl, currentlyFree, socialSharingProps, nativeShareText }) {
  const { loading, forceRefreshData, audiobook } = useGraphQLClientData(graphQLQueries.audiobookForAudiobookButtons, {
    id: audiobookId,
  });

  const handleAddItemToCart = useContext(ShoppingCartContext);

  function renderGiftAudiobookButton() {
    return <GiftAudiobookButton audiobookId={audiobookId} giftPurchaseUrl={giftPurchaseUrl} condensed small />;
  }

  function renderSpacer() {
    return (
      <div className={styles.spacerWrapper}>
        <div className={styles.spacer}></div>
      </div>
    );
  }

  function handleSavedToWishlistClick() {
    bubEvents.track('Wishlist button', 'Clicked site signup element');

    forceRefreshData();
  }

  function renderWishlistButton() {
    return (
      <WishlistButton
        audiobookId={audiobookId}
        inCart={!_.isNil(audiobook.currentUserShoppingCartItem)}
        inWishlist={!_.isNil(audiobook.currentUserWishlistItem)}
        onClick={handleSavedToWishlistClick}
        condensed
        small
      />
    );
  }

  function renderShareButton() {
    return <GeneralSharingButton
            audiobook={audiobook}
            small
            nativeShareText={nativeShareText}
            socialSharingProps={socialSharingProps}
          />;
  }

  function renderOptions() {
    if (!currentlyFree) {
      return (
        <div className={classNames(styles.buttonRow, styles.grid)}>
          <div className={styles.buttonColumn}>{renderWishlistButton()}</div>
          {renderSpacer()}
          <div className={styles.buttonColumn}>{renderGiftAudiobookButton()}</div>
          {renderSpacer()}
          <div className={styles.buttonColumn}>{renderShareButton()}</div>
        </div>
      );
    }

    return <div className={classNames(styles.buttonRow, styles.singleButton)}>{renderWishlistButton()}</div>;
  }

  if (loading) {
    return <Loader />;
  }

  if (!audiobook) {
    return null;
  }

  return (
    <div className={styles.audiobookButtons} data-testid='audiobook-buttons'>
      <AddToCartButton
        audiobook={audiobook}
        inCart={!_.isNil(audiobook.currentUserShoppingCartItem)}
        inWishlist={!_.isNil(audiobook.currentUserWishlistItem)}
        trackingCategory='Audiobook details'
        onClick={forceRefreshData}
        handleAddItemToCart={handleAddItemToCart}
      />
      <div className={styles.audiobookButtonsRow}>{renderOptions()}</div>
    </div>
  );
}

AudiobookButtons.propTypes = {
  audiobookId: PropTypes.string.isRequired,
  giftPurchaseUrl: PropTypes.string.isRequired,
  currentlyFree: PropTypes.bool.isRequired,
  socialSharingProps: PropTypes.object,
  nativeShareText: PropTypes.string,
};

AudiobookButtons.defaultProps = {
  socialSharingProps: {},
};
